import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, X, Heart } from "lucide-react";
import { api } from "@/lib/api";

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const txId = params.get("tx") || params.get("payment_intent");
  const lookupId = txId || sessionId;
  // Stripe redirects back with these params after a wallet (Apple Pay /
  // Google Pay) confirms. `redirect_status=succeeded` is Stripe's own
  // assertion that the payment was authorised — we trust it immediately.
  // Same with `instant=1` (set by our InlineCheckout when stripe.confirmPayment
  // returns successfully WITHOUT a redirect — most card / Apple Pay flows).
  const redirectStatus = params.get("redirect_status");
  const instant = params.get("instant") === "1";
  const piStatus = params.get("pi_status");
  const stripeAlreadyConfirmed = (
    instant ||
    redirectStatus === "succeeded" ||
    redirectStatus === "pending" ||
    piStatus === "succeeded" ||
    piStatus === "processing"
  );
  const [state, setState] = useState({
    // INSTANT thank-you when Stripe.js already told us the payment confirmed.
    // No polling, no spinner. The Stripe SDK on the frontend knows the result
    // the moment the bank approves — there is no reason to make the donor wait.
    status: stripeAlreadyConfirmed ? "paid" : "polling",
    data: null,
  });

  useEffect(() => {
    if (!lookupId) {
      if (stripeAlreadyConfirmed) return; // success page already showing
      setState({ status: "error", data: null });
      return;
    }
    // If Stripe already confirmed, do a SINGLE background fetch to enrich
    // the thank-you page with the actual donation amount + designation.
    // The visitor doesn't wait for it; the page is already showing success.
    if (stripeAlreadyConfirmed) {
      api.get(`/donations/status/${lookupId}`)
        .then((r) => setState((s) => ({ ...s, data: r.data })))
        .catch(() => {});
      return;
    }
    // Fallback: card flow that didn't go through our InlineCheckout (legacy
    // Stripe Checkout session redirect). Poll until paid or timeout.
    let attempts = 0;
    const maxAttempts = 30;
    const poll = async () => {
      try {
        const r = await api.get(`/donations/status/${lookupId}`);
        const d = r.data;
        if (d.payment_status === "paid" || d.status === "succeeded") {
          setState({ status: "paid", data: d });
          return;
        }
        if (d.payment_status === "processing") {
          setState({ status: "paid", data: d, processing: true });
          if (attempts < maxAttempts) { attempts++; setTimeout(poll, 3000); }
          return;
        }
        if (d.status === "expired") {
          setState({ status: "expired", data: d });
          return;
        }
        attempts++;
        if (attempts >= maxAttempts) {
          setState({ status: "timeout", data: d, processing: true });
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        attempts++;
        if (attempts >= maxAttempts) {
          setState({ status: "timeout", data: null, processing: true });
          return;
        }
        setTimeout(poll, 3000);
      }
    };
    poll();
  }, [lookupId, stripeAlreadyConfirmed]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="donate-success-page">
      <div className="max-w-lg w-full rounded-3xl bg-white border border-sand p-10 md:p-14 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 size={40} className="text-emerald_deep animate-spin mx-auto" />
            <h1 className="font-serif text-3xl mt-6 text-ink">Confirming your gift…</h1>
            <p className="mt-3 text-ink/60 font-light">Please wait a moment while we confirm with Stripe.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald_deep text-gold flex items-center justify-center mx-auto"><Check size={32} /></div>
            <div className="font-arabic text-3xl text-gold mt-6">جزاك الله خيرًا</div>
            <h1 className="font-serif text-4xl mt-3 text-ink">Thank you!</h1>
            {state.data?.amount ? (
              <p className="mt-4 text-ink/70 font-light text-lg">
                Your gift of <span className="font-semibold text-emerald_deep">${state.data.amount.toFixed(2)}</span>
                {state.data.designation ? <> to <span className="font-semibold text-emerald_deep">{state.data.designation}</span></> : null} has been received.
              </p>
            ) : (
              <p className="mt-4 text-ink/70 font-light text-lg">
                Your donation has been received. May Allah accept it.
              </p>
            )}
            {state.processing ? (
              <p className="mt-3 text-sm text-ink/55">Your receipt will arrive by email shortly.</p>
            ) : (
              <p className="mt-3 text-sm text-ink/50">May Allah accept your sadaqah. A receipt has been emailed.</p>
            )}
            <Link to="/" data-testid="back-home-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald_deep text-cream px-6 py-3 text-sm font-semibold uppercase tracking-wider">
              <Heart size={14} /> Back to home
            </Link>
          </>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <>
            <div className="w-16 h-16 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto"><X size={32} /></div>
            <h1 className="font-serif text-3xl mt-6 text-ink">Something went wrong</h1>
            <p className="mt-3 text-ink/60 font-light">Your payment was not completed. Please try again.</p>
            <Link to="/donate" className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald_deep text-cream px-6 py-3 text-sm font-semibold">Try again</Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto"><Loader2 size={32} className="animate-spin" /></div>
            <h1 className="font-serif text-3xl mt-6 text-ink">Payment received — finalising</h1>
            <p className="mt-3 text-ink/60 font-light">
              Your bank is taking a moment to confirm. You can safely close this page —
              your receipt will arrive by email within a few minutes.
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald_deep text-cream px-6 py-3 text-sm font-semibold">Back to home</Link>
          </>
        )}
      </div>
    </div>
  );
}
